import type { BuilderChannel, BuilderFormat } from "../config";

// Country groups mirror the legacy flyer-type restrictions (src/utils/isValidFlyerType):
// A4/A5/A6 are offered across ANZ + EU; DL is ANZ-only; US/CA get a single channel format.
const ANZ_COUNTRIES = ["AU", "NZ"];
const EU_COUNTRIES = ["FR", "NL", "UK", "BE", "CH", "DE", "ES", "IT", "PL", "LU", "PT"];

const FORMAT: Record<string, BuilderFormat> = {
  TEARAWAY: { value: "TEARAWAY", label: "Tearaway" },
  DOOR_HANGER: { value: "DOOR_HANGER", label: "Door Hanger" },
  A4: { value: "A4", label: "A4" },
  A5: { value: "A5", label: "A5" },
  A6: { value: "A6", label: "A6", isDefault: true },
  DL: { value: "DL", label: "DL" },
};

// A6/A5/A4 (+ DL in ANZ) — the multi-format markets. A6 is the default everywhere it appears.
const anzFormats = (): BuilderFormat[] => [FORMAT.A6, FORMAT.A5, FORMAT.A4, FORMAT.DL];
const euFormats = (): BuilderFormat[] => [FORMAT.A6, FORMAT.A5, FORMAT.A4];

// Build the per-country map for a flyering channel: US/CA → the channel's single format;
// ANZ/EU → the multi-format list (dropdown).
const flyeringCountries = (single: BuilderFormat): Record<string, BuilderFormat[]> => {
  const byCountry: Record<string, BuilderFormat[]> = {
    US: [{ ...single, isDefault: true }],
    CA: [{ ...single, isDefault: true }],
  };
  ANZ_COUNTRIES.forEach((code) => (byCountry[code] = anzFormats()));
  EU_COUNTRIES.forEach((code) => (byCountry[code] = euFormats()));
  return byCountry;
};

// International ADM launch catalogue — flat pieces only (folded formats deferred).
// Format values double as the CountryADMPrintingCosts label key, mirroring how the
// US postcards work, so a format only quotes when the country has a price row for it.
const admIntlFormats = (): BuilderFormat[] => [
  { value: "A6", label: "A6 (14.8 × 10.5 cm)", isDefault: true },
  { value: "DL", label: "DL (21.0 × 9.9 cm)" },
  { value: "A5", label: "A5 (21.0 × 14.8 cm)" },
  { value: "A4", label: "A4 (21.0 × 29.7 cm)" },
];

const CATALOG: Partial<Record<BuilderChannel, Record<string, BuilderFormat[]>>> = {
  ADM: {
    US: [
      { value: "POSTCARD_6x4", label: '4" x 6" Postcard' },
      { value: "POSTCARD_9x6", label: '6" x 9" Postcard', isDefault: true },
      { value: "POSTCARD_11x6", label: '6" x 11" Postcard' },
      { value: "POSTCARD_15x12", label: '12" x 15" Postcard' },
    ],
    AU: admIntlFormats(),
  },
  // US/CA Flyering → Tearaway only; US/CA D2D → Door Hanger only (single-format disclaimer).
  // Other markets offer A6/A5/A4 (+ DL in ANZ) via a dropdown.
  H2H: flyeringCountries(FORMAT.TEARAWAY),
  D2D: flyeringCountries(FORMAT.DOOR_HANGER),
};

// Fail-closed: anything not in the catalog returns no formats.
export const getFormatsFor = (channel: BuilderChannel, countryCode: string): BuilderFormat[] =>
  CATALOG[channel]?.[countryCode] ?? [];
